<template>
  <template v-if="rendering">
    <ResultsList
      v-if="!slideshow"
      :records="records"
      :kind="kind"
      :payload="payload"
      :settings="settings"
      :loading="loading"
      :paginated="paginated"
      :commands="commands"
      :search-url="requestKey"
    >
      <slot name="list-start"></slot>
    </ResultsList>
    <ResultsEmpty v-if="empty" :parts="noResultsParts" @add-filters="handleAddFilters" />
    <ResultsPagination
      v-if="paginated"
      :page="payload.page"
      :page-count="payload.page_count"
      :per-page="payload.per_page"
      :prev-icon="paginationPrevIcon"
      :next-icon="paginationNextIcon"
      @show-page="handleShowPage"
      @show-per-page="handleShowPerPage"
    />
  </template>
  <ResultsSlideshow
    v-if="slideshowRendering"
    :records="records"
    :kind="kind"
    :payload="payload"
    :settings="settings"
    :commands="commands"
  />
  <slot />
</template>

<script setup>
import { computed, ref } from "vue";
import { whenI18nReady } from "#ce/components/SpFinder/i18n";
import { useFinderResults } from "#ce/components/SpFinder/composables/useFinderResults";
import { useExpose } from "#ce/composables/useExpose";
import { useRenderedResults } from "#ce/apps/SpFinderResultView/composables/useRenderedResults";
import ResultsEmpty from "#ce/apps/SpFinderResultView/components/ResultsEmpty.ce.vue";
import ResultsList from "#ce/apps/SpFinderResultView/components/ResultsList.ce.vue";
import ResultsPagination from "#ce/apps/SpFinderResultView/components/ResultsPagination.ce.vue";
import ResultsSlideshow from "#ce/apps/SpFinderResultView/components/ResultsSlideshow.ce.vue";

defineProps({
  /** The `sp-icon` name of the previous-page control; a deployment sets it in `ui/components.yml`. */
  paginationPrevIcon: {
    type: String,
    default: "chevron-left-filled",
  },
  /** The `sp-icon` name of the next-page control; a deployment sets it in `ui/components.yml`. */
  paginationNextIcon: {
    type: String,
    default: "chevron-right-filled",
  },
});

const catalogsReady = ref(false);

function markCatalogsReady() {
  catalogsReady.value = true;
}

whenI18nReady.then(markCatalogsReady);

const { kind, settings, payload, records, loading, empty, commands, requestKey } = useFinderResults();

const rendering = computed(() => catalogsReady.value && Boolean(payload.value));
const slideshow = computed(() => settings.value?.listLayout === "slideshow");
const slideshowRendering = computed(() => rendering.value && slideshow.value);
const paginated = computed(() => !slideshow.value && records.value.length > 0 && Boolean(settings.value?.pagination));
const noResultsParts = computed(() => payload.value?.no_results?.parts ?? []);

const { whenRendered } = useRenderedResults();
const { exposeMethods } = useExpose();

exposeMethods({ whenRendered });

function handleAddFilters(filters) {
  commands.addFilters(filters);
}

function handleShowPage(page) {
  commands.showPage(page);
}

function handleShowPerPage(perPage) {
  commands.showPerPage(perPage);
}
</script>

<style>
:host {
  display: block;
}

/* `preview` shows the top of the list and fades it out, for an embed that opens collapsed. */
:host([preview]) .results-list {
  --fade-height: var(--sp-comp-finder-results-preview-fade-height, 200px);

  max-height: var(--sp-comp-finder-results-preview-height, 750px);
  overflow: hidden;
  mask-image: linear-gradient(
    to bottom,
    #000 calc(100% - var(--fade-height)),
    transparent calc(100% - var(--fade-height) / 2)
  );
}
</style>
